import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AutoGraph,
  CheckCircle,
  FactCheck,
  Handshake,
  Hub,
  Inventory2,
  MenuBook,
  RocketLaunch,
  Search,
  Security,
  Storefront,
  TipsAndUpdates,
} from '@mui/icons-material';
import { headerGradient } from '../theme';

type SolutionCard = {
  title: string;
  trl: string;
  segment: string;
  theme: string;
  problem: string;
  solution: string;
  benefits: string[];
  status: string;
  access: string;
  patent: {
    titular: string;
    depositNumber: string;
    depositDate: string;
    grantDate: string;
    internationalDeposit: string;
    inventors: string;
  };
};

const segmentFilters = ['Todos', 'Comércio', 'Serviços', 'Turismo', 'Outros'];
const themeFilters = [
  'Todos',
  'Sistemas de Gestão',
  'Aplicativos',
  'Soluções com IA',
  'Plataformas Digitais',
  'Ferramentas de análise de dados',
  'Outros',
];

const practicalBenefits = [
  'Conhecer soluções já desenvolvidas',
  'Identificar oportunidades de melhoria no negócio',
  'Solicitar adaptações conforme sua realidade',
  'Desenvolver novas soluções em parceria',
  'Utilizar tecnologias já disponíveis por transferência de tecnologia',
];

const audienceItems = [
  'Empresas do comércio de bens, serviços e turismo',
  'Pequenos e médios negócios',
  'Empreendedores que buscam inovação',
  'Organizações que desejam desenvolver ou aplicar soluções tecnológicas',
];

const processSteps = [
  {
    title: 'Identificação do desafio',
    description: 'A empresa identifica uma necessidade ou oportunidade de melhoria em seu negócio.',
  },
  {
    title: 'Busca ou cadastra uma demanda',
    description: 'Pode explorar soluções já disponíveis na Vitrine ou cadastrar sua própria demanda.',
  },
  {
    title: 'Conexão com o Senac AM',
    description:
      'O time de inovação realiza a conexão entre a empresa e alunos, docentes ou pesquisadores responsáveis pelas soluções.',
  },
  {
    title: 'Desenvolvimento ou adaptação',
    description: 'A solução é desenvolvida ou ajustada conforme a necessidade da empresa.',
  },
  {
    title: 'Implementação e resultados',
    description: 'A tecnologia é aplicada no negócio, gerando melhorias reais e impacto direto.',
  },
];

const exploreActions = [
  {
    title: 'Explorar soluções',
    description: 'Ver tecnologias já desenvolvidas e entender como elas podem ajudar sua empresa.',
    icon: <Search />,
  },
  {
    title: 'Cadastrar uma demanda',
    description: 'Descrever um problema ou necessidade do seu negócio.',
    icon: <FactCheck />,
  },
  {
    title: 'Desenvolver uma solução',
    description: 'Trabalhar junto com o Senac para criar algo sob medida.',
    icon: <Handshake />,
  },
  {
    title: 'Utilizar uma solução pronta',
    description: 'Aplicar uma tecnologia já desenvolvida, com apoio técnico.',
    icon: <RocketLaunch />,
  },
];

const solutions: SolutionCard[] = [
  {
    trl: 'TRL 6-7',
    segment: 'Comércio',
    theme: 'Sistemas de Gestão',
    title: 'Sistema de cadastro e contagem automatizada de produtos (Double Check)',
    problem:
      'Empresas do atacado e varejo enfrentam dificuldades no controle de estoque, com erros de contagem, retrabalho e divergências entre o sistema e a quantidade real de produtos.',
    solution:
      'Ferramenta digital que apoia o cadastro e a conferência de produtos, utilizando um modelo de dupla verificação para aumentar a precisão das informações.',
    benefits: [
      'Redução de erros na contagem de estoque',
      'Mais confiabilidade nas informações',
      'Agilidade nos processos de conferência',
      'Apoio à gestão e tomada de decisão',
    ],
    status: 'Em fase de validação em ambiente real',
    access: 'Transferência de tecnologia ou desenvolvimento em parceria',
    patent: {
      titular: 'Senac Amazonas',
      depositNumber: 'A informar',
      depositDate: 'A informar',
      grantDate: 'A informar',
      internationalDeposit: 'A informar',
      inventors: 'A informar',
    },
  },
  {
    trl: 'TRL 6-7',
    segment: 'Serviços',
    theme: 'Soluções com IA',
    title: 'Sistema de apoio à tomada de decisão com Inteligência Artificial',
    problem:
      'Empresas têm dificuldade em analisar dados e tomar decisões rápidas e assertivas, o que pode impactar resultados e competitividade.',
    solution:
      'Sistema que utiliza Inteligência Artificial para apoiar a análise de informações e auxiliar na tomada de decisão no negócio.',
    benefits: [
      'Apoio à tomada de decisão',
      'Melhor uso de dados do negócio',
      'Mais agilidade nas análises',
      'Aumento da eficiência operacional',
    ],
    status: 'Em fase de validação em ambiente real',
    access: 'Transferência de tecnologia ou desenvolvimento em parceria',
    patent: {
      titular: 'Senac Amazonas',
      depositNumber: 'A informar',
      depositDate: 'A informar',
      grantDate: 'A informar',
      internationalDeposit: 'A informar',
      inventors: 'A informar',
    },
  },
];

const patentSections = [
  {
    title: 'O que é uma patente',
    description:
      'Patente é um tipo de proteção para uma solução ou invenção. Ela garante que quem criou a tecnologia tenha o direito de usar, comercializar ou autorizar o uso por outras pessoas ou empresas.',
    items: ['Protege uma ideia que virou solução prática', 'Ajuda a organizar direitos de uso e exploração da tecnologia'],
  },
  {
    title: 'Nem toda ideia pode ser patenteada',
    description: 'Para uma solução ser patenteada, ela precisa atender a três pontos principais.',
    items: ['Ser nova, sem existir anteriormente', 'Ser diferente do comum, sem ser algo óbvio', 'Ter aplicação prática e funcionar na vida real'],
  },
  {
    title: 'O que normalmente não pode ser patenteado',
    description: 'Algumas criações seguem outros caminhos de proteção ou não se enquadram como patente.',
    items: ['Ideias abstratas', 'Métodos de ensino ou de negócios', 'Descobertas naturais', 'Programas de computador isolados, normalmente protegidos por direito autoral'],
  },
  {
    title: 'O que significa ser novo',
    description:
      'Uma ideia só é considerada nova quando ela não faz parte do que já existe no mundo. Se já foi divulgada, mesmo que você não conhecesse, pode não ser nova.',
    items: ['Outras patentes', 'Produtos no mercado', 'Sites, vídeos ou apresentações', 'Qualquer informação já divulgada'],
  },
  {
    title: 'Atenção antes de divulgar sua ideia',
    description:
      'Se você divulgar sua ideia antes do registro, pode perder a chance de patentear. No Brasil, existe um prazo de até 12 meses após divulgação feita pelo próprio inventor, mas isso deve ser analisado com cuidado.',
    items: ['Se possível, proteja antes de divulgar', 'Evite publicar ou apresentar antes do registro', 'Tenha cuidado ao compartilhar a ideia', 'Procure orientação antes de divulgar'],
  },
  {
    title: 'Como avaliar se sua ideia pode ser protegida',
    description: 'Antes de registrar, uma análise ajuda a entender se vale a pena seguir com o pedido.',
    items: [
      'Essa solução realmente resolve um problema técnico?',
      'Já existe algo parecido?',
      'O que muda em relação ao que já existe?',
      'Essa diferença é relevante ou apenas um detalhe?',
    ],
  },
  {
    title: 'Por que isso é importante para sua empresa',
    description: 'Registrar uma patente pode fortalecer a estratégia do negócio.',
    items: ['Evitar cópias', 'Gerar novas fontes de renda', 'Valorizar o negócio', 'Aumentar a competitividade', 'Abrir portas para parcerias'],
  },
  {
    title: 'Patente também é inteligência de mercado',
    description:
      'Patentes não servem apenas para proteger. Elas também são uma fonte rica de informação para apoiar decisões estratégicas e evitar retrabalho.',
    items: [
      'Mostram o que outras empresas estão criando',
      'Indicam tendências de mercado e tendências tecnológicas',
      'Revelam tecnologias em crescimento',
      'Ajudam a entender o que já existe e o que ainda pode ser inovado',
    ],
  },
  {
    title: 'Quando faz sentido pensar em patente',
    description: 'Se a solução resolve um problema real e é diferente do que já existe no mercado, pode ter potencial de proteção.',
    items: ['Um produto novo', 'Um sistema ou solução tecnológica', 'Um processo diferente de fazer algo', 'Uma melhoria inovadora em algo que já existe'],
  },
  {
    title: 'Como funciona o registro',
    description:
      'O registro de patente é feito junto ao INPI e garante a proteção de uma invenção, assegurando direitos de uso e exploração da tecnologia.',
    items: ['Verificar se a ideia já existe', 'Descrever a solução de forma clara', 'Fazer o pedido de registro', 'Aguardar a análise técnica', 'Receber a concessão, se aprovado'],
  },
  {
    title: 'Marcas também são importantes',
    description:
      'Além da patente, sua empresa pode registrar a marca, como nome, logotipo e identidade do negócio. Isso protege a marca no mercado e evita usos semelhantes por outras empresas.',
    items: ['Proteção do nome e da identidade do negócio', 'Redução de riscos de uso indevido', 'Fortalecimento da presença no mercado'],
  },
  {
    title: 'Apoio do Senac Amazonas',
    description: 'O Senac Amazonas pode orientar sobre caminhos para proteção da propriedade intelectual, conforme a natureza do projeto.',
    items: ['Avaliação inicial da solução', 'Orientação sobre próximos passos', 'Apoio na organização das informações', 'Conexão com projetos e iniciativas de inovação'],
  },
  {
    title: 'Patentes e a Vitrine Tecnológica',
    description: 'Na Vitrine, empresas e parceiros podem aproximar inovação, proteção intelectual e aplicação prática.',
    items: ['Conhecer soluções com potencial de proteção', 'Participar do desenvolvimento de tecnologias', 'Utilizar soluções já desenvolvidas', 'Conectar inovação com o seu negócio'],
  },
];

const patentLinks = [
  {
    label: 'Lei nº 9.279/1996 - Propriedade Industrial',
    href: 'https://www.planalto.gov.br/ccivil_03/leis/l9279.htm',
  },
  {
    label: 'Consulta Base de Dados do INPI',
    href: 'https://busca.inpi.gov.br/pePI/servlet/LoginController?action=login',
  },
  {
    label: 'Minha Primeira Patente (INPI)',
    href: 'https://www.gov.br/inpi/pt-br/servicos/patentes/minha-primeira-patente',
  },
  {
    label: 'Manual de Marcas - INPI',
    href: 'https://manualdemarcas.inpi.gov.br/',
  },
];

const transferCards = [
  {
    title: 'O que significa na prática',
    description:
      'A transferência de tecnologia acontece quando uma empresa passa a utilizar uma solução já desenvolvida, em vez de criar algo do zero.',
  },
  {
    title: 'Como acontece no Senac',
    description:
      'Soluções prontas ou em estágio avançado podem ser disponibilizadas para uso quando há interesse de uma empresa, com apoio da equipe responsável.',
  },
  {
    title: 'Formas de utilização',
    description:
      'A tecnologia pode ser aplicada diretamente, ajustada à realidade do negócio ou evoluída em conjunto com o Senac.',
  },
  {
    title: 'Papel do Senac',
    description:
      'O Senac atua como parceiro, apoiando a empresa na compreensão da solução, adaptação à sua realidade e implementação da tecnologia.',
  },
];

const transferTypes = [
  'Sistemas de gestão',
  'Aplicativos',
  'Ferramentas digitais',
  'Processos e métodos de trabalho',
  'Soluções com tecnologia e inovação aplicada',
];

const transferAdvantages = [
  'Ganhar tempo',
  'Reduzir custos',
  'Diminuir riscos',
  'Melhorar processos',
  'Aprimorar atendimento e resultados',
];

const transferMore = [
  'Regras para a Transferência de Tecnologia',
  'Registro da Propriedade Intelectual',
  'Política de Inovação',
  'Patentes de Invenção e Modelos de Utilidade',
  'Projetos de Desenvolvimento Tecnológico e Inovação',
];

const SectionTitle: React.FC<{ eyebrow?: string; title: string; centered?: boolean; width?: number }> = ({
  eyebrow,
  title,
  centered = false,
  width = 210,
}) => (
  <Box sx={{ textAlign: centered ? 'center' : 'left', mb: { xs: 2.5, md: 3 } }}>
    {eyebrow && (
      <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 800, letterSpacing: 1 }}>
        {eyebrow}
      </Typography>
    )}
    <Typography
      variant="h4"
      sx={{
        fontWeight: 800,
        color: 'primary.main',
        lineHeight: 1.2,
        fontSize: { xs: '1.55rem', sm: '1.85rem', md: '2.125rem' },
        position: 'relative',
        pb: 1.2,
        '&::after': {
          content: '""',
          position: 'absolute',
          left: centered ? '50%' : 0,
          transform: centered ? 'translateX(-50%)' : 'none',
          bottom: 0,
          width: { xs: Math.min(width, 240), sm: width },
          height: 3,
          bgcolor: 'secondary.main',
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const BulletList: React.FC<{ items: string[]; color?: string }> = ({ items, color = 'text.secondary' }) => (
  <Box component="ul" sx={{ m: 0, pl: { xs: 2.1, md: 2.4 } }}>
    {items.map((item) => (
      <Typography component="li" key={item} sx={{ color, lineHeight: 1.75, mb: 0.6 }}>
        {item}
      </Typography>
    ))}
  </Box>
);

const VitrineTecnologica: React.FC = () => {
  const theme = useTheme();
  const [segmentFilter, setSegmentFilter] = useState('Todos');
  const [themeFilter, setThemeFilter] = useState('Todos');

  const filteredSolutions = useMemo(
    () =>
      solutions.filter((solution) => {
        const matchesSegment = segmentFilter === 'Todos' || solution.segment === segmentFilter;
        const matchesTheme = themeFilter === 'Todos' || solution.theme === themeFilter;
        return matchesSegment && matchesTheme;
      }),
    [segmentFilter, themeFilter]
  );

  return (
    <Box sx={{ pt: { xs: 3, md: 9 }, pb: { xs: 6, md: 8 }, backgroundColor: '#f7f8fb' }}>
      <Box
        sx={{
          mx: { xs: 0, md: -4 },
          mt: { xs: -3, md: -9 },
          px: { xs: 2, sm: 3, md: 8 },
          py: { xs: 4.5, md: 8 },
          background: headerGradient,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: { xs: -90, md: 40 },
            bottom: { xs: -80, md: -70 },
            width: { xs: 220, md: 360 },
            height: { xs: 220, md: 360 },
            border: '1px solid rgba(255,255,255,0.18)',
            transform: 'rotate(18deg)',
          },
        }}
      >
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={7}>
            <Chip
              label="Pesquisa e Inovação"
              sx={{ mb: 2, color: '#fff', borderColor: 'rgba(255,255,255,0.46)' }}
              variant="outlined"
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                lineHeight: 1.08,
                fontSize: { xs: '2.05rem', sm: '2.6rem', md: '3.75rem' },
              }}
            >
              Vitrine Tecnológica Senac-AM
            </Typography>
            <Typography sx={{ mt: { xs: 2, md: 2.5 }, color: 'rgba(255,255,255,0.88)', lineHeight: 1.8, maxWidth: 760, fontSize: { xs: '0.96rem', md: '1rem' } }}>
              Soluções, projetos e tecnologias de inovação aplicada desenvolvidas por alunos, docentes e pesquisadores,
              conectando desafios reais do comércio de bens, serviços e turismo a respostas práticas e aplicáveis.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
              <Button href="#explore" variant="contained" color="secondary" sx={{ fontWeight: 700, width: { xs: '100%', sm: 'auto' } }}>
                Explorar Vitrine
              </Button>
              <Button href="#solucoes" variant="outlined" color="inherit" sx={{ fontWeight: 700, width: { xs: '100%', sm: 'auto' } }}>
                Ver Soluções
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              sx={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.22)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <Storefront />
                  <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.25rem', md: '1.5rem' }, lineHeight: 1.25 }}>
                    Uma vitrine para inovação aplicada
                  </Typography>
                </Stack>
                <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.88)', lineHeight: 1.75 }}>
                  Como uma vitrine de loja, mas no lugar de produtos, apresenta ferramentas, sistemas, ideias e projetos
                  desenvolvidos para resolver problemas reais dos negócios.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box id="sobre" component="section" sx={{ px: { xs: 0, sm: 1, md: 2 }, mt: { xs: 5.5, md: 7 }, scrollMarginTop: 96 }}>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid item xs={12} md={7}>
            <SectionTitle eyebrow="Sobre" title="O que é uma Vitrine Tecnológica?" width={260} />
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.85 }}>
              Uma vitrine tecnológica funciona como uma vitrine de loja, mas em vez de produtos, apresenta soluções e
              tecnologias que podem ser utilizadas por empresas. Nela, você pode conhecer ferramentas, sistemas, ideias e
              projetos desenvolvidos para resolver problemas reais do dia a dia dos negócios.
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.85 }}>
              A Vitrine Tecnológica Senac-AM apresenta soluções, projetos e tecnologias de inovação aplicada desenvolvidas
              por alunos, docentes e pesquisadores da instituição. Seu objetivo é aproximar o Senac do mercado, conectando
              desafios reais a soluções práticas, inovadoras e aplicáveis.
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.85 }}>
              Por meio da Vitrine, empresas podem conhecer tecnologias prontas, demonstrar interesse em soluções e
              cadastrar demandas para o desenvolvimento de novos projetos, fortalecendo a inovação e o desenvolvimento
              regional.
            </Typography>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderColor: alpha(theme.palette.primary.main, 0.1),
                boxShadow: '0 18px 38px rgba(15, 23, 42, 0.08)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800, mb: 2 }}>
                  Na prática, sua empresa pode
                </Typography>
                <BulletList items={practicalBenefits} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, mb: 2, fontSize: { xs: '1.35rem', md: '1.5rem' } }}>
            Para quem é a Vitrine
          </Typography>
          <Grid container spacing={2}>
            {audienceItems.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item}>
                <Card variant="outlined" sx={{ height: '100%', borderColor: alpha(theme.palette.secondary.main, 0.18) }}>
                  <CardContent sx={{ p: { xs: 2.2, md: 2.5 } }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <CheckCircle color="secondary" fontSize="small" />
                      <Typography sx={{ color: 'text.secondary', lineHeight: 1.55 }}>{item}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Box id="funcionamento" component="section" sx={{ mt: { xs: 5.5, md: 8 }, py: { xs: 4.5, md: 6 }, mx: { xs: 0, md: -4 }, backgroundColor: '#fff' }}>
        <Box sx={{ px: { xs: 2, sm: 3, md: 6 } }}>
          <SectionTitle eyebrow="Funcionamento" title="Conectamos soluções do Senac AM a desafios do mercado" centered width={360} />
          <Grid container spacing={2.5}>
            {processSteps.map((step, index) => (
              <Grid item xs={12} md={index < 2 ? 6 : 4} key={step.title}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderColor: alpha(theme.palette.primary.main, 0.1),
                    borderTop: `4px solid ${theme.palette.secondary.main}`,
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                    <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 800 }}>
                      Etapa {index + 1}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ mt: 1.2, color: 'text.secondary', lineHeight: 1.75 }}>{step.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Box id="explore" component="section" sx={{ mt: { xs: 5.5, md: 8 }, scrollMarginTop: 96 }}>
        <SectionTitle eyebrow="Explore a Vitrine" title="Escolha como sua empresa quer inovar" centered width={300} />
        <Grid container spacing={2.5}>
          {exploreActions.map((action) => (
            <Grid item xs={12} sm={6} md={3} key={action.title}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: alpha(theme.palette.primary.main, 0.08),
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
                }}
              >
                <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                  <Box sx={{ color: 'secondary.main', mb: 1.4 }}>{action.icon}</Box>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    {action.title}
                  </Typography>
                  <Typography sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.7 }}>{action.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ mt: 3, background: headerGradient, color: '#fff', boxShadow: '0 16px 34px rgba(0, 58, 112, 0.16)' }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.5rem' } }}>
                  Seu projeto pode ser o próximo
                </Typography>
                <Typography sx={{ mt: 1.4, color: 'rgba(255,255,255,0.88)', lineHeight: 1.75 }}>
                  Se sua empresa enfrenta desafios que podem ser resolvidos com tecnologia, cadastre sua demanda e
                  conecte-se com nossos alunos e docentes.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  component="a"
                  href="https://forms.cloud.microsoft/pages/responsepage.aspx?id=VYz8K3TflEuoWng2wpkRuPYfZfsFGudFmwlB0TiqPd1URTdGS1REMkpEUU1ONVlHSjZZTjg3QVo1Qi4u&route=shorturl"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="secondary"
                  fullWidth
                  sx={{ py: 1.4, fontWeight: 800 }}
                >
                  Cadastrar Demanda
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Box id="solucoes" component="section" sx={{ mt: { xs: 5.5, md: 8 }, scrollMarginTop: 96 }}>
        <SectionTitle eyebrow="Soluções Desenvolvidas" title="Projetos em exposição" centered width={220} />

        <Card variant="outlined" sx={{ mb: 3, borderColor: alpha(theme.palette.primary.main, 0.1) }}>
          <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800, mb: 1.5 }}>
              Filtro por Segmento
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ '& .MuiChip-root': { height: 'auto' }, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
            >
              {segmentFilters.map((filter) => (
                <Chip
                  key={filter}
                  label={filter}
                  clickable
                  color={segmentFilter === filter ? 'primary' : 'default'}
                  variant={segmentFilter === filter ? 'filled' : 'outlined'}
                  onClick={() => setSegmentFilter(filter)}
                />
              ))}
            </Stack>

            <Divider sx={{ my: 2.4 }} />

            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800, mb: 1.5 }}>
              Filtro por temática
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ '& .MuiChip-root': { height: 'auto' }, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
            >
              {themeFilters.map((filter) => (
                <Chip
                  key={filter}
                  label={filter}
                  clickable
                  color={themeFilter === filter ? 'secondary' : 'default'}
                  variant={themeFilter === filter ? 'filled' : 'outlined'}
                  onClick={() => setThemeFilter(filter)}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {filteredSolutions.map((solution) => (
            <Grid item xs={12} md={6} key={solution.title}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: alpha(theme.palette.primary.main, 0.1),
                  boxShadow: '0 16px 34px rgba(15, 23, 42, 0.07)',
                }}
              >
                <CardContent sx={{ p: { xs: 2.4, md: 3.5 } }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mb: 1.5, '& .MuiChip-root': { height: 'auto' }, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4 } }}
                  >
                    <Chip size="small" label={solution.trl} color="secondary" />
                    <Chip size="small" label={`Segmento: ${solution.segment}`} variant="outlined" />
                    <Chip size="small" label={solution.theme} variant="outlined" />
                  </Stack>
                  <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, lineHeight: 1.25, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    {solution.title}
                  </Typography>

                  <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.75 }}>
                    <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>
                      Problema:{' '}
                    </Box>
                    {solution.problem}
                  </Typography>
                  <Typography sx={{ mt: 1.5, color: 'text.secondary', lineHeight: 1.75 }}>
                    <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>
                      Solução:{' '}
                    </Box>
                    {solution.solution}
                  </Typography>

                  <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 800, mt: 2 }}>
                    Benefícios
                  </Typography>
                  <BulletList items={solution.benefits} />

                  <Box sx={{ mt: 2, p: { xs: 1.6, md: 2 }, backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>
                        Status:{' '}
                      </Box>
                      {solution.status}
                    </Typography>
                    <Typography sx={{ mt: 0.8, color: 'text.secondary', lineHeight: 1.7 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>
                        Acesso:{' '}
                      </Box>
                      {solution.access}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2.5 }} />

                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    Patente
                  </Typography>
                  <Grid container spacing={1.2} sx={{ mt: 0.5 }}>
                    {Object.entries({
                      Titular: solution.patent.titular,
                      'Número de Depósito': solution.patent.depositNumber,
                      'Data de Depósito': solution.patent.depositDate,
                      'Data de Concessão': solution.patent.grantDate,
                      'Depósito Internacional': solution.patent.internationalDeposit,
                      Inventores: solution.patent.inventors,
                    }).map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>
                            {label}:{' '}
                          </Box>
                          {value}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>

                  <Button component={RouterLink} to="/contato" variant="contained" color="primary" sx={{ mt: 2.5, fontWeight: 800, width: { xs: '100%', sm: 'auto' } }}>
                    Tenho interesse
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredSolutions.length === 0 && (
          <Card variant="outlined" sx={{ borderColor: alpha(theme.palette.primary.main, 0.1) }}>
            <CardContent>
              <Typography sx={{ color: 'text.secondary' }}>
                Nenhuma solução encontrada para os filtros selecionados.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      <Box id="patentes" component="section" sx={{ mt: { xs: 5.5, md: 8 }, py: { xs: 4.5, md: 6 }, mx: { xs: 0, md: -4 }, backgroundColor: '#fff' }}>
        <Box sx={{ px: { xs: 2, sm: 3, md: 6 } }}>
          <SectionTitle eyebrow="Registro de Marcas e Patentes" title="Proteção para soluções e identidade do negócio" centered width={420} />
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 860, mx: 'auto', mb: 4, lineHeight: 1.85 }}>
            Antes de divulgar ou aplicar uma tecnologia no mercado, é importante entender se ela pode ser protegida e qual
            caminho faz mais sentido: patente, marca, direito autoral ou outra estratégia de propriedade intelectual.
          </Typography>

          <Grid container spacing={2.5}>
            {patentSections.map((section) => (
              <Grid item xs={12} md={6} key={section.title}>
                <Card variant="outlined" sx={{ height: '100%', borderColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                    <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ mb: 1 }}>
                      <Security color="secondary" fontSize="small" />
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                        {section.title}
                      </Typography>
                    </Stack>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 1.4 }}>{section.description}</Typography>
                    <BulletList items={section.items} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card variant="outlined" sx={{ mt: 3, borderColor: alpha(theme.palette.secondary.main, 0.18) }}>
            <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800, mb: 2 }}>
                Saiba mais
              </Typography>
              <Grid container spacing={1.5}>
                {patentLinks.map((link) => (
                  <Grid item xs={12} sm={6} key={link.href}>
                    <Button
                      component="a"
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      sx={{ justifyContent: 'flex-start', py: 1.2 }}
                    >
                      {link.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box id="transferencia" component="section" sx={{ mt: { xs: 5.5, md: 8 }, scrollMarginTop: 96 }}>
        <SectionTitle eyebrow="Transferência de Tecnologia" title="Use soluções já desenvolvidas" centered width={300} />
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 820, mx: 'auto', mb: 4, lineHeight: 1.85 }}>
          A transferência de tecnologia permite aplicar, no seu negócio, tecnologias criadas no Senac que já têm finalidade
          prática e foram pensadas para resolver problemas reais.
        </Typography>

        <Grid container spacing={2.5}>
          {transferCards.map((card, index) => (
            <Grid item xs={12} md={6} key={card.title}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: alpha(theme.palette.primary.main, 0.1),
                  borderLeft: `4px solid ${index % 2 === 0 ? theme.palette.secondary.main : theme.palette.primary.main}`,
                }}
              >
                <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    {index % 2 === 0 ? <TipsAndUpdates color="secondary" /> : <Hub color="primary" />}
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                      {card.title}
                    </Typography>
                  </Stack>
                  <Typography sx={{ mt: 1.3, color: 'text.secondary', lineHeight: 1.75 }}>{card.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%', borderColor: alpha(theme.palette.primary.main, 0.1) }}>
              <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ mb: 1.5 }}>
                  <Inventory2 color="secondary" />
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    O que pode ser transferido
                  </Typography>
                </Stack>
                <BulletList items={transferTypes} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%', borderColor: alpha(theme.palette.primary.main, 0.1) }}>
              <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ mb: 1.5 }}>
                  <AutoGraph color="secondary" />
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    Vantagens para sua empresa
                  </Typography>
                </Stack>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 1.5 }}>
                  Inovar de forma mais simples e acessível, utilizando uma solução já desenvolvida para reduzir esforços e
                  acelerar melhorias.
                </Typography>
                <BulletList items={transferAdvantages} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ mt: 3, background: headerGradient, color: '#fff' }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.5rem' } }}>
                  Próximo passo
                </Typography>
                <Typography sx={{ mt: 1.4, color: 'rgba(255,255,255,0.88)', lineHeight: 1.75 }}>
                  Se alguma solução da Vitrine fizer sentido para o seu negócio, entre em contato com a equipe do Senac
                  para entender como ela pode ser aplicada na prática.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Button component={RouterLink} to="/contato" variant="contained" color="secondary" fullWidth sx={{ py: 1.4, fontWeight: 800 }}>
                  Falar com o Senac AM
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mt: 3, borderColor: alpha(theme.palette.primary.main, 0.1) }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ mb: 1.5 }}>
              <MenuBook color="secondary" />
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                Saiba mais
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              {transferMore.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Chip
                    label={item}
                    variant="outlined"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      py: 0.8,
                      '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center' },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VitrineTecnologica;
